import { newSearch } from "./actions"
import { initAutocomplete } from "./autocomplete/initAutocomplete"
import { Config, resolveQuery } from "./config"
import { isErrorSession } from "./helpers/errorHandling"
import { getFullUrl, getSearchUrl } from "./urlEncoding/url"
import { initSerp } from "./serp"
import { createStore } from "./state"
import { measure } from "@nosto/search-js/utils"
import { nostojs } from "@nosto/nosto-js"
import { registerSearchTermsProvider } from "./api/nosto"
import { makeConfig } from "./fullConfig"

/**
 * Create search page
 *
 * @example
 * ```
 * import { init } from '@nosto/preact'
 *
 * import serpComponent from './serp'
 *
 * init({
 *     serpComponent,
 *     inputCssSelector: '#search',
 *     contentCssSelector: '#content',
 *     merchant: '123',
 *     defaultInput: {
 *         products: {
 *             size: 30
 *         }
 *     }
 * })
 * ```
 */
export async function init(config: Config): Promise<void> {
    const end = measure("init")
    const fullConfig = makeConfig(config)
    const api = await new Promise(nostojs)
    const { searchEnabled, categoriesEnabled } = api.internal.getSettings()

    const searchActive = searchEnabled && fullConfig.contentCssSelector
    const autocompleteActive = searchEnabled && fullConfig.inputCssSelector
    const categoriesActive = categoriesEnabled && fullConfig.categoryCssSelector

    if (searchActive || autocompleteActive) {
        registerSearchTermsProvider(fullConfig)
        const serpStore = createStore({
            query: resolveQuery(fullConfig, "serpQuery"),
            pageType: "search"
        })
        if (searchActive) {
            initSerp(fullConfig, serpStore)
        }
        if (autocompleteActive) {
            initAutocomplete(fullConfig, {
                onSubmit: (query, options) => {
                    if (query.query) {
                        nostojs(api => api.recordSearchSubmit(query.query!))
                    }
                    if (fullConfig.serpPathRedirect || (fullConfig.fallback && isErrorSession())) {
                        if (typeof fullConfig.serpPathRedirect === "function") {
                            fullConfig.serpPathRedirect(query, options)
                        } else {
                            location.href = getFullUrl(getSearchUrl({ config: fullConfig, store: serpStore }, query))
                        }
                    } else {
                        newSearch({ config: fullConfig, store: serpStore }, query, options)
                    }
                }
            })
        }
    }

    if (categoriesActive) {
        await api.pageTaggingAsync()

        if (fullConfig.isCategoryPage()) {
            const categoryStore = createStore({
                query: resolveQuery(fullConfig, "categoryQuery"),
                pageType: "category"
            })
            initSerp(fullConfig, categoryStore)
        }
    }
    end()
}
